import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useCreateOperationCategoryMutation,
    useDeleteOperationCategoryMutation,
    useOperationCategoriesQuery,
    useUpdateOperationCategoryMutation,
} from '@/api/queries';
import { PATH } from '@/router/PATH';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canEditOperations,
    canReadOperations,
    deriveOperationsAccess,
    type OperationsAccess,
} from '../operationsAccess';
import {
    EMPTY_CATEGORY_FORM,
    blockingOperationCount,
    isCategoryFormSubmittable,
    refusalMessage,
    toCategoryRows,
    type CategoryFormState,
    type CategoryRow,
} from './categoriesMeta';

export type UseCategoriesPageResult = {
    access: OperationsAccess;
    /** Whether the set is readable at all — the page renders a notice if not. */
    canRead: boolean;
    /** Whether this session may change it. A BranchAdmin reads and edits nothing. */
    canEdit: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    /** A failed **read**. A refused write is not this — see the two message fields. */
    error: ErrorResponse | null;
    onRetry: () => void;
    /** `null` until the first load resolves — never an empty array meanwhile. */
    rows: CategoryRow[] | null;
    onBack: () => void;

    /** The open name form, or `null`. One form for both adding and renaming. */
    form: CategoryFormState | null;
    onAdd: () => void;
    onRename: (id: string) => void;
    onFormNameKaChange: (value: string) => void;
    onFormNameEnChange: (value: string) => void;
    onFormCancel: () => void;
    onFormSubmit: () => void;
    /** Whether the form has a name in at least one language. */
    isFormSubmittable: boolean;
    isSaving: boolean;
    /** The server's refusal on the form, in the server's own words. */
    formError: string | null;

    /** The row a delete is being confirmed for, or `null`. */
    deleteTarget: CategoryRow | null;
    onDelete: (id: string) => void;
    onDeleteCancel: () => void;
    onDeleteConfirm: () => void;
    isDeleting: boolean;
    /** The server's refusal on the delete, in the server's own words. */
    deleteError: string | null;
    /**
     * How many operations are blocking the delete, when the refusal said so.
     * `null` for every refusal that is not about a count — "this is the last
     * category", most obviously.
     */
    blockingCount: number | null;
};

/**
 * Drives the category management screen.
 *
 * **The list is the response, not a local copy.** Create and rename both answer
 * with the whole set in display order, and the mutation invalidates the
 * operations key, so the screen re-renders from the server rather than splicing
 * a row into an order it does not own. A delete answers nothing and the refetch
 * does the same job.
 *
 * **Access is derived before fetching**, the pattern every accounting screen
 * here follows: a PIN/kiosk token is refused by every one of these endpoints
 * whatever role it carries, so a page that fetched in order to find out would
 * show a failure where the truth is a permission.
 *
 * **A refused write is not the page's error.** `error` is a failed *read* and
 * the container throws it to the boundary, because a list that will not load is
 * a screen that cannot be trusted. A refusal — the delete a template is blocking,
 * a name that was blank — is expected, actionable, and belongs beside the
 * control that caused it, in the server's own words.
 */
export const useCategoriesPage = (): UseCategoriesPageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const navigate = useNavigate();

    // Read once per mount, like the other accounting screens: the session cannot
    // change without a navigation that remounts this page.
    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadOperations(access);
    const canEdit = canEditOperations(access);

    const [form, setForm] = React.useState<CategoryFormState | null>(null);
    const [deleteId, setDeleteId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    const [blockingCount, setBlockingCount] = React.useState<number | null>(
        null,
    );

    const query = useOperationCategoriesQuery(canRead);
    const { data, error } = unwrapStatement(query.data, query.error);

    const create = useCreateOperationCategoryMutation();
    const update = useUpdateOperationCategoryMutation();
    const remove = useDeleteOperationCategoryMutation();

    const rows = React.useMemo(
        () => (data ? toCategoryRows(data, locale) : null),
        [data, locale],
    );

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    const onBack = React.useCallback(() => {
        navigate(PATH.ACCOUNTING.OPERATIONS.TEMPLATES);
    }, [navigate]);

    // ── the name form ───────────────────────────────────────────────────────

    const onAdd = React.useCallback(() => {
        setFormError(null);
        setForm(EMPTY_CATEGORY_FORM);
    }, []);

    const onRename = React.useCallback(
        (id: string) => {
            const row = rows?.find((candidate) => candidate.id === id);
            if (!row) {
                return;
            }
            setFormError(null);
            // Seeded from the row's raw names rather than its display label: the
            // label may have fallen back to the other language, and saving that
            // would silently copy one language's name over the other's.
            setForm({ id, nameKa: row.nameKa, nameEn: row.nameEn });
        },
        [rows],
    );

    const onFormNameKaChange = React.useCallback((value: string) => {
        setForm((current) => (current ? { ...current, nameKa: value } : null));
    }, []);

    const onFormNameEnChange = React.useCallback((value: string) => {
        setForm((current) => (current ? { ...current, nameEn: value } : null));
    }, []);

    const onFormCancel = React.useCallback(() => {
        setForm(null);
        setFormError(null);
    }, []);

    const onFormSubmit = React.useCallback(() => {
        if (!form || !isCategoryFormSubmittable(form)) {
            return;
        }
        setFormError(null);
        // Trimmed here as well as on the server: what the user typed with a
        // trailing space is not a different category, and sending it would have
        // the response come back looking edited.
        const body = {
            nameKa: form.nameKa.trim(),
            nameEn: form.nameEn.trim(),
        };
        const onSettled = (result: {
            success: boolean;
            error?: ErrorResponse;
        }) => {
            if (result.success) {
                setForm(null);
                return;
            }
            setFormError(refusalMessage(result.error));
        };

        if (form.id === null) {
            create.mutate(body, { onSuccess: onSettled });
            return;
        }
        update.mutate({ id: form.id, body }, { onSuccess: onSettled });
    }, [create, form, update]);

    // ── removing ────────────────────────────────────────────────────────────

    const onDelete = React.useCallback((id: string) => {
        setDeleteError(null);
        setBlockingCount(null);
        setDeleteId(id);
    }, []);

    const onDeleteCancel = React.useCallback(() => {
        setDeleteId(null);
        setDeleteError(null);
        setBlockingCount(null);
    }, []);

    const onDeleteConfirm = React.useCallback(() => {
        if (!deleteId) {
            return;
        }
        setDeleteError(null);
        setBlockingCount(null);
        remove.mutate(deleteId, {
            onSuccess: (result) => {
                if (result.success) {
                    setDeleteId(null);
                    return;
                }
                // The dialog stays open on a refusal. It is where the reason
                // belongs — the operations standing in the way are named in the
                // message, and closing it would leave the user pressing the same
                // button again to find out why nothing happened.
                setDeleteError(refusalMessage(result.error));
                setBlockingCount(blockingOperationCount(result.error));
            },
        });
    }, [deleteId, remove]);

    const deleteTarget = rows?.find((row) => row.id === deleteId) ?? null;

    return {
        access,
        canRead,
        canEdit,
        locale,
        isLoading: query.isLoading,
        error,
        onRetry,
        rows,
        onBack,

        form,
        onAdd,
        onRename,
        onFormNameKaChange,
        onFormNameEnChange,
        onFormCancel,
        onFormSubmit,
        isFormSubmittable: form ? isCategoryFormSubmittable(form) : false,
        isSaving: create.isPending || update.isPending,
        formError,

        deleteTarget,
        onDelete,
        onDeleteCancel,
        onDeleteConfirm,
        isDeleting: remove.isPending,
        deleteError,
        blockingCount,
    };
};
